import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { COMPANY } from "@/lib/constants";

export default function Login() {
  const { user, login, register, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // customer email mode
  const [admin, setAdmin] = useState({ email: "", password: "" });
  const [cust, setCust] = useState({ name: "", email: "", password: "" });

  useEffect(() => {
    if (user && user.role) navigate(user.role === "admin" ? "/admin" : "/portal", { replace: true });
  }, [user, navigate]);

  const doAdmin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(admin.email, admin.password);
      navigate(u.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doCustomer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login" ? await login(cust.email, cust.password) : await register(cust.name, cust.email, cust.password);
      navigate(u.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative bg-secondary grain-texture">
        <img src="/archive/login-hero.png" alt="Stamped concrete walkway and fire-pit patio at sunset by CG Concrete in Watertown, WI" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/45 to-secondary/10" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3">
            <img src="/archive/logo.jpg" alt="CG Concrete" className="h-14 w-14 rounded-sm object-contain bg-white p-0.5" />
            <span className="font-heading font-extrabold text-xl drop-shadow">CG CONCRETE LLC</span>
          </Link>
          <div>
            <span className="inline-flex items-center gap-2 bg-primary/25 border border-primary/40 px-3 py-1 text-xs font-bold tracking-wider mb-4 backdrop-blur-sm">WATERTOWN, WI</span>
            <h2 className="font-heading text-4xl font-extrabold leading-tight drop-shadow-lg">Breathtaking outdoor spaces, poured to last.</h2>
            <p className="text-white/85 mt-4 max-w-md drop-shadow">CG Concrete LLC crafts the driveways, patios, and fire-pit retreats Watertown gathers around — and helps you manage every project from quote to completion.</p>
          </div>
          <span className="text-white/60 text-sm drop-shadow">© {new Date().getFullYear()} {COMPANY.name}</span>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-8"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
          <h1 className="font-heading text-3xl font-extrabold">Welcome back</h1>
          <p className="text-muted-foreground mt-1 mb-6">Sign in to your account.</p>

          {user && (
            <div className="mb-4 text-sm bg-muted p-3 flex items-center justify-between">
              <span>Signed in as {user.email}</span>
              <button onClick={logout} className="font-bold text-primary" data-testid="switch-account">Switch account</button>
            </div>
          )}

          <Tabs defaultValue="customer">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="customer" data-testid="tab-customer">Customer</TabsTrigger>
              <TabsTrigger value="admin" data-testid="tab-admin">Admin</TabsTrigger>
            </TabsList>

            <TabsContent value="customer" className="pt-6">
              <Button onClick={googleLogin} variant="outline" className="w-full h-12 rounded-sm font-bold" data-testid="google-login-btn">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
                Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">or with email</span><div className="flex-1 h-px bg-border" /></div>
              <form onSubmit={doCustomer} className="space-y-4">
                {mode === "register" && (
                  <div><Label>Name</Label><Input value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="cust-name" /></div>
                )}
                <div><Label>Email</Label><Input type="email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} className="mt-1.5 rounded-sm" data-testid="cust-email" /></div>
                <div><Label>Password</Label><Input type="password" value={cust.password} onChange={(e) => setCust({ ...cust, password: e.target.value })} className="mt-1.5 rounded-sm" data-testid="cust-password" /></div>
                <Button type="submit" disabled={busy} className="w-full h-11 bg-primary hover:bg-primary/90 rounded-sm font-bold" data-testid="cust-submit">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-4 text-center">
                {mode === "login" ? "New customer?" : "Already have an account?"}{" "}
                <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-bold text-primary" data-testid="toggle-mode">
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </p>
            </TabsContent>

            <TabsContent value="admin" className="pt-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4"><ShieldCheck className="h-4 w-4 text-primary" /> Staff access only</div>
              <form onSubmit={doAdmin} className="space-y-4">
                <div><Label>Email</Label><Input type="email" value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.target.value })} className="mt-1.5 rounded-sm" data-testid="admin-email" placeholder="admin@cgconcretellc.com" /></div>
                <div><Label>Password</Label><Input type="password" value={admin.password} onChange={(e) => setAdmin({ ...admin, password: e.target.value })} className="mt-1.5 rounded-sm" data-testid="admin-password" /></div>
                <Button type="submit" disabled={busy} className="w-full h-11 bg-secondary hover:bg-secondary/90 rounded-sm font-bold" data-testid="admin-submit">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In as Admin"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
